// PostHog product analytics.
//
// We use posthog-js (not posthog-react-native) because Capacitor runs the
// app inside a WebView — the JS SDK is what we need. The native lifecycle
// edge case is event flushing on app backgrounding: WebViews don't reliably
// fire visibilitychange or beforeunload when the OS backgrounds the
// activity, so we hook @capacitor/app's appStateChange and call
// posthog.capture / flush ourselves.
//
// PII discipline:
//   - identify() takes only userId + persona + age_bucket + signup_date.
//   - We never pass name, email, exact DOB, location, IDs of other users,
//     or any other personal field to PostHog.
//
// Set VITE_POSTHOG_API_KEY (and optionally VITE_POSTHOG_HOST) in .env to
// enable. When unset, every track() / identify() call is a cheap no-op.

import posthog from "posthog-js";
import { App as CapacitorApp } from "@capacitor/app";
import type { AppState } from "@capacitor/app";
import { mobileAnalytics } from "@/lib/mobileAnalytics";
import { isNativePlatform } from "@/lib/platform";
import type { SubscriptionTier, WaitlistPlan } from "@/lib/billing/types";
import type { BillingMode } from "@/lib/featureFlags";

/** Tier unions used in event payloads. Aliased off the billing types so
 *  adding a tier updates the analytics schema automatically. */
type AnyTier = SubscriptionTier;
type PaidTier = WaitlistPlan;

const API_KEY = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://us.i.posthog.com";

let initialised = false;

export type Persona = "player" | "scout" | "academy";
export type AgeBucket = "13-17" | "18-24" | "25-34" | "35+";

export type AnalyticsEvent =
  | { name: "user_signed_up"; props: { persona: Persona; signup_method: "email" | "google"; age_bucket: AgeBucket | "unknown" } }
  | { name: "email_verified"; props?: Record<string, never> }
  | { name: "video_uploaded"; props: { uploader: "player" | "academy"; size_bucket: "small" | "medium" | "large" } }
  | { name: "strength_card_generated"; props?: { score_bucket?: "low" | "mid" | "high" } }
  | { name: "player_profile_viewed"; props?: { viewer_persona?: Persona } }
  | { name: "scout_searched"; props: { has_query: boolean; filter_count: number } }
  | { name: "scout_viewed_player"; props?: Record<string, never> }
  | { name: "academy_connected_player"; props?: Record<string, never> }
  | { name: "ai_coach_query"; props?: { chip_type?: string } }
  | { name: "account_deleted"; props: { persona: Persona } }
  | { name: "forgot_password_requested"; props?: Record<string, never> }
  | { name: "parental_consent_requested"; props?: Record<string, never> }
  | { name: "parental_consent_approved"; props?: Record<string, never> }
  // Feed feature (Module 2). Deliberately no post_id / user_id props —
  // those would identify content and breach our identified-only-via-
  // identify() PII rule. text_length is the raw char count; if we ever
  // want to bucket it later we'll add a helper here.
  | { name: "feed_viewed"; props?: Record<string, never> }
  | { name: "feed_compose_opened"; props?: Record<string, never> }
  | { name: "post_created"; props: { text_length: number; has_image: boolean } }
  | { name: "post_liked"; props?: Record<string, never> }
  | { name: "post_unliked"; props?: Record<string, never> }
  | { name: "post_deleted"; props?: Record<string, never> }
  // Feed feature (Module 3) — social layer.
  //
  // PII posture: target_user_id and profile_user_id ARE user-identifying,
  // but the actor is identified via posthog.identify() already, and
  // attributing follows / list opens is needed to measure Discovery and
  // EmptyFeed conversion. The id values are uuids (not emails / names)
  // and never appear in person-profile properties — only in events.
  //
  // `source` mirrors FollowButton's FollowButtonSource union exactly;
  // bumping the FollowButton variant requires bumping this too.
  | {
      name: "follow_initiated";
      props: {
        target_user_id: string;
        source:
          | "profile"
          | "feed_empty"
          | "discovery"
          | "followers_list"
          | "following_list"
          | "suggested";
      };
    }
  | {
      name: "unfollow_initiated";
      props: {
        target_user_id: string;
        source:
          | "profile"
          | "feed_empty"
          | "discovery"
          | "followers_list"
          | "following_list"
          | "suggested";
      };
    }
  | { name: "discovery_step_shown"; props?: Record<string, never> }
  | { name: "discovery_step_followed_count"; props: { count: number } }
  | { name: "discovery_step_skipped"; props?: Record<string, never> }
  // Fired when the user opens DiscoverPlayers via the top-bar UserPlus
  // icon (as opposed to the post-onboarding entry, which fires
  // discovery_step_shown). source is fixed today but kept as a union
  // so we can split entry points later (e.g. feed-empty CTA).
  | { name: "discover_icon_tapped"; props: { source: "top_bar" | "empty_feed" } }
  // Public profile view (Group D). Fires when a player opens another
  // player's PlayerViewProfile (not their own — self-view bounces to
  // home before this fires). video_id IS included for play events
  // because per-video performance is operationally useful; profile_user_id
  // is included on both so we can attribute video plays back to whose
  // profile drove them.
  | { name: "public_profile_viewed"; props: { profile_user_id: string } }
  | { name: "public_video_played"; props: { video_id: string; profile_user_id: string } }
  // Guided tour (Group E). persona discriminates the two flows.
  | { name: "tour_started"; props: { persona: "player" | "scout" } }
  | { name: "tour_completed"; props: { persona: "player" | "scout" } }
  | { name: "tour_skipped"; props: { persona: "player" | "scout" } }
  | { name: "tour_restarted_from_settings"; props: { persona: "player" | "scout" } }
  | {
      name: "feed_empty_suggested_player_followed";
      props: { target_user_id: string };
    }
  | { name: "scout_feed_viewed"; props?: Record<string, never> }
  | { name: "followers_list_opened"; props: { profile_user_id: string } }
  | { name: "following_list_opened"; props: { profile_user_id: string } }
  // Billing / Pricing module (v1.0 pre-billing). target_plan_id is
  // never user-identifying; tier values match the database CHECK list.
  | {
      name: "pricing_page_viewed";
      props: {
        source:
          | "settings"
          | "limit_reached"
          | "profile"
          | "leaderboard"
          | "upload_indicator"
          | "other";
      };
    }
  | { name: "pricing_plan_card_expanded"; props: { plan_id: PaidTier } }
  | { name: "pricing_period_toggled"; props: { period: "monthly" | "annual" } }
  | { name: "waitlist_joined"; props: { target_plan_id: PaidTier } }
  | { name: "waitlist_left"; props: { target_plan_id: PaidTier } }
  | {
      name: "ai_scoring_limit_reached";
      props: { tier: AnyTier; used: number };
    }
  | { name: "ai_scoring_limit_modal_upgrade_clicked"; props?: Record<string, never> }
  // web_info billing mode. paywall_viewed fires once per paywall mount;
  // upgrade_intent fires on the tap that records interest, including
  // repeat taps inside the 24h dedupe window (the user still asked).
  | { name: "paywall_viewed"; props: { mode: BillingMode; tier: AnyTier } }
  | { name: "upgrade_intent"; props: { tier: PaidTier } };

/** Compute a coarse age bucket from age in years. Never use the raw age — bucketed only. */
export function ageToBucket(age: number | null): AgeBucket | "unknown" {
  if (age == null) return "unknown";
  if (age < 18) return "13-17";
  if (age < 25) return "18-24";
  if (age < 35) return "25-34";
  return "35+";
}

/** Coarse size bucket for video uploads — keep raw bytes off the wire. */
export function sizeToBucket(bytes: number): "small" | "medium" | "large" {
  const mb = bytes / 1024 / 1024;
  if (mb < 25) return "small";
  if (mb < 100) return "medium";
  return "large";
}

export function initAnalytics(): void {
  if (initialised) return;
  if (!API_KEY) {
    // eslint-disable-next-line no-console
    console.warn("[analytics] VITE_POSTHOG_API_KEY unset — analytics disabled");
    return;
  }
  posthog.init(API_KEY, {
    api_host: HOST,
    // We track explicitly via track(); turn off broad autocapture so PII
    // (e.g. label text) doesn't leak.
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    persistence: "localStorage+cookie",
    person_profiles: "identified_only",
    // Don't write IPs or UA into the ingest if we can avoid it.
    ip: false,
    disable_session_recording: true,
  });
  initialised = true;

  // Capacitor lifecycle: flush on backgrounding so events don't get lost
  // when the OS evicts the WebView.
  try {
    void CapacitorApp.addListener("appStateChange", (state: AppState) => {
      if (!state.isActive) {
        try { posthog.capture("$pageleave"); } catch { /* ignore */ }
        // posthog-js doesn't expose a sync flush; the capture above pushes
        // queued events into the next batch send, which the SDK fires on
        // its own schedule.
      }
    });
  } catch {
    // Plugin not available (web build) — fall back to visibilitychange.
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          try { posthog.capture("$pageleave"); } catch { /* ignore */ }
        }
      });
    }
  }
}

export function identifyUser(opts: {
  userId: string;
  persona: Persona;
  age_bucket: AgeBucket | "unknown";
  signup_date?: string; // ISO date
}): void {
  if (!initialised) return;
  // Whitelist exactly the fields we want on the person profile — no PII.
  posthog.identify(opts.userId, {
    persona: opts.persona,
    age_bucket: opts.age_bucket,
    signup_date: opts.signup_date,
  });
}

export function resetAnalytics(): void {
  if (!initialised) return;
  posthog.reset();
}

export function track<T extends AnalyticsEvent>(event: T): void {
  if (!initialised) return;
  try {
    posthog.capture(event.name, event.props ?? {});
  } catch {
    // Never let analytics throw into the call site.
  }
}

// ===========================================================================
// Google Analytics 4 (gtag.js on web) + Firebase Analytics (native).
//
// This is a SEPARATE channel from the PostHog `track()` system above — the
// two coexist. GA4 is wired via the gtag snippet in index.html; on native
// every event also forwards to Firebase Analytics (mobileAnalytics).
// `analytics.*` calls are no-ops where the respective SDK isn't present, so
// they're always safe to call.
// ===========================================================================

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer?: any[];
  }
}

const GA_ID = "G-VFC47NWRBJ";

export const analytics = {
  /** SPA page view — called from App.tsx on every route change. */
  pageView: (path: string, title?: string) => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: path,
        page_title: title,
        page_location: typeof window !== "undefined" ? window.location.href : undefined,
      });
    }
  },

  /** Identify the signed-in user (GA4 + Firebase). */
  setUser: (userId: string, userRole: string) => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("config", GA_ID, { user_id: userId });
      window.gtag("set", "user_properties", { role: userRole });
    }
    if (isNativePlatform()) {
      void mobileAnalytics.setUser(userId, userRole).catch(() => {});
    }
  },

  /** Generic event — fires on web (GA4) AND native (Firebase). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  event: (eventName: string, params?: Record<string, any>) => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }
    if (isNativePlatform()) {
      void mobileAnalytics.event(eventName, params).catch(() => {});
    }
  },

  // ---- Academy events ----
  academy: {
    signUp: () => analytics.event("academy_signup"),
    videoUploaded: (drillType: string) => analytics.event("video_uploaded", { drill_type: drillType }),
    reportGenerated: (playerCount: number) => analytics.event("report_generated", { player_count: playerCount }),
    planUpgradeClicked: (planName: string) => analytics.event("plan_upgrade_clicked", { plan_name: planName }),
    paymentCompleted: (planName: string, amountInr: number) =>
      analytics.event("purchase", {
        transaction_id: Date.now().toString(),
        value: amountInr,
        currency: "INR",
        items: [{ item_name: planName }],
      }),
    playerTagged: () => analytics.event("player_tagged"),
    noteAdded: () => analytics.event("coach_note_added"),
    aiLabOpened: (feature: string) => analytics.event("ai_lab_opened", { feature }),
    askSportsBioQuery: () => analytics.event("ask_sportsbio_query"),
  },

  // ---- Player events ----
  player: {
    signUp: () => analytics.event("player_signup"),
    strengthCardViewed: () => analytics.event("strength_card_viewed"),
    strengthCardShared: (method: string) => analytics.event("strength_card_shared", { method }),
    strengthCardDownloaded: () => analytics.event("strength_card_downloaded"),
    profileUpdated: () => analytics.event("player_profile_updated"),
    videoUploaded: () => analytics.event("player_video_uploaded"),
    subscriptionStarted: (planName: string) => analytics.event("player_subscription_started", { plan_name: planName }),
  },

  // ---- Scout events ----
  scout: {
    signUp: () => analytics.event("scout_signup"),
    playerSearched: () => analytics.event("scout_player_searched"),
    playerShortlisted: () => analytics.event("scout_player_shortlisted"),
    strengthCardViewed: () => analytics.event("scout_strength_card_viewed"),
    subscriptionStarted: (planName: string) => analytics.event("scout_subscription_started", { plan_name: planName }),
  },
};
